import { Handshake, Loader2, Sparkles, TrendingUp, Trophy } from "lucide-react";
import { formatCurrencyEUR } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import type {
  ContentEngagementSummary,
  DealPipelineSummary,
  NotificationEngagementSummary,
  TimelineEvent,
} from "@colab/shared";
import { KpiTile } from "./KpiTile";
import { DealStageBarChart } from "./DealStageBarChart";
import { ContentEngagementCard } from "./ContentEngagementCard";
import { NotificationEngagementCard } from "./NotificationEngagementCard";
import { ActivityTimeline } from "./ActivityTimeline";

// Structural subset of TanStack's UseQueryResult. The view types on this
// minimum shape (not the full UseQueryResult) so admin sub-bucket 10 can
// reuse the view without forcing TanStack as a transitive dep — any caller
// that produces `{ data, isLoading, isError }` works.
export type QueryFacade<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
};

export function PartnerAnalyticsView({
  pipeline,
  content,
  notifications,
  timeline,
  rangeLabel,
}: {
  pipeline: QueryFacade<DealPipelineSummary>;
  content: QueryFacade<ContentEngagementSummary>;
  notifications: QueryFacade<NotificationEngagementSummary>;
  timeline: QueryFacade<TimelineEvent[]>;
  rangeLabel: string;
}) {
  return (
    <div className="space-y-6">
      {/* KPI row — sourced from loadDealPipeline.totals */}
      {pipeline.isLoading ? (
        <KpiSkeletonRow />
      ) : pipeline.isError || !pipeline.data ? (
        <ErrorPanel label="Couldn't load pipeline" />
      ) : (
        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiTile
            label="Deals"
            value={pipeline.data.totals.deals.current.toLocaleString()}
            delta={pipeline.data.totals.deals}
            icon={Handshake}
            hint="Deals referred to Bookboost"
          />
          <KpiTile
            label="Wins"
            value={pipeline.data.totals.wins.current.toLocaleString()}
            delta={pipeline.data.totals.wins}
            icon={Trophy}
            hint="Closed-won in window"
          />
          <KpiTile
            label="ARR"
            value={formatCurrencyEUR(pipeline.data.totals.arr.current)}
            delta={pipeline.data.totals.arr}
            icon={TrendingUp}
            hint="EUR only — mixed currency excluded"
          />
          <KpiTile
            label="Intros"
            value={pipeline.data.totals.intros.current.toLocaleString()}
            delta={pipeline.data.totals.intros}
            icon={Sparkles}
            hint="Leads Bookboost sent to partner"
          />
        </section>
      )}

      {/* Bar chart — sourced from loadDealPipeline.byStage (same query) */}
      <section className="rounded-xl border bg-card p-5">
        <header className="mb-4">
          <h2 className="font-display text-base font-semibold tracking-tight">
            Pipeline by stage
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Inbound HubSpot deals attributed to this partner, distributed
            across the Sales pipeline. Window: {rangeLabel.toLowerCase()}.
          </p>
        </header>
        {pipeline.isLoading ? (
          <Skeleton className="h-[260px] w-full rounded-md" />
        ) : pipeline.isError || !pipeline.data ? (
          <ErrorPanel label="Couldn't load pipeline" inline />
        ) : (
          <DealStageBarChart data={pipeline.data.byStage} />
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        {content.isLoading ? (
          <CardSkeleton heading="Content engagement" />
        ) : content.isError || !content.data ? (
          <ErrorPanel label="Couldn't load content engagement" />
        ) : (
          <ContentEngagementCard summary={content.data} />
        )}

        {notifications.isLoading ? (
          <CardSkeleton heading="Notifications" />
        ) : notifications.isError || !notifications.data ? (
          <ErrorPanel label="Couldn't load notifications" />
        ) : (
          <NotificationEngagementCard summary={notifications.data} />
        )}
      </div>

      {timeline.isLoading ? (
        <TimelineSkeleton />
      ) : timeline.isError || !timeline.data ? (
        <ErrorPanel label="Couldn't load activity" />
      ) : (
        <ActivityTimeline events={timeline.data} />
      )}
    </div>
  );
}

function KpiSkeletonRow() {
  return (
    <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-[108px] rounded-xl" />
      ))}
    </section>
  );
}

function CardSkeleton({ heading }: { heading: string }) {
  return (
    <section className="rounded-xl border bg-card p-5">
      <header className="mb-4">
        <h2 className="font-display text-base font-semibold tracking-tight">
          {heading}
        </h2>
      </header>
      <div className="space-y-3">
        <Skeleton className="h-16 w-full rounded-md" />
        <Skeleton className="h-3 w-3/4 rounded-md" />
        <Skeleton className="h-3 w-1/2 rounded-md" />
      </div>
    </section>
  );
}

function TimelineSkeleton() {
  return (
    <section className="rounded-xl border bg-card p-5">
      <header className="mb-4">
        <h2 className="font-display text-base font-semibold tracking-tight">
          Activity
        </h2>
      </header>
      <ul className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <li key={i} className="flex items-start gap-3">
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-2/3 rounded-md" />
              <Skeleton className="h-2.5 w-1/3 rounded-md" />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ErrorPanel({ label, inline }: { label: string; inline?: boolean }) {
  return (
    <section
      className={
        inline
          ? "flex h-[260px] items-center justify-center rounded-md border border-dashed bg-muted/20 text-xs text-muted-foreground"
          : "rounded-xl border bg-card p-10 text-center"
      }
    >
      {!inline && <Loader2 className="mx-auto h-8 w-8 text-muted-foreground" />}
      <h2
        className={
          inline
            ? "inline-flex items-center gap-2"
            : "mt-3 font-display text-base font-semibold tracking-tight"
        }
      >
        {label}
      </h2>
      {!inline && (
        <p className="mt-1 text-sm text-muted-foreground">
          Please refresh the page. If the problem persists, contact support.
        </p>
      )}
    </section>
  );
}
